import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractComponent,
  Channel,
  ChannelService,
  ComponentCanDeactivate,
  ConfirmationComponent,
  WA_TEMPLATE_LIMITS,
  WaTemplateRead,
} from 'lib-trend-core';
import { map, Observable, takeUntil } from 'rxjs';
import { isTemplateEditable } from './template-editability';
import { ORDER_DETAILS_CATEGORY, TemplateFormEngine, TemplateHeaderFormatOption, TemplateRuleProblem } from './template-form-engine';

/**
 * Create and edit a WhatsApp Cloud API message template.
 *
 * One page, one form — no wizard. Only Marketing → Standard is offered, so there is nothing to
 * choose before writing the template: the category is fixed and the outcome is reported the way
 * every other form in this project reports it, with an alert and a return to the list.
 *
 * The page has a second mode, reached with `?format=pix` (DEV-1992): it builds the Payments API -
 * Brazil template, the one Meta's own composer cannot create because the format is API-only. Only
 * three things differ — the category, the `displayFormat`, and a button set fixed to the single
 * ORDER_DETAILS button — so it is a mode of this form rather than a second form to keep in step.
 *
 * SPEC-2026-08-whatsapp-cloud-api-template-builder.
 */
@Component({
    selector: 'template-builder-component',
    templateUrl: './template-builder.component.html',
    styleUrls: ['./template-builder.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TemplateBuilderComponent extends AbstractComponent implements OnInit, ComponentCanDeactivate {

  readonly limits = WA_TEMPLATE_LIMITS;

  channel: Channel;
  template: WaTemplateRead;
  engine: TemplateFormEngine;

  editing: boolean = false;

  /** Payments API - Brazil mode, selected by `?format=pix` on the create route (DEV-1992). */
  pixMode: boolean = false;

  /** Rules that a field validator cannot express, refreshed as the form changes (RF-28). */
  problems: Array<TemplateRuleProblem> = [];

  submitting: boolean = false;

  /** Set once saved, so leaving afterwards asks nothing (RF-03). */
  private saved: boolean = false;

  /**
   * Read synchronously from the route in ngOnInit. The exit navigation must not depend on
   * `channel`, which arrives asynchronously and stays undefined forever if the load fails —
   * `Router.navigate` rejects a nullish segment outright, which would leave the user stuck.
   */
  idChannel: string;
  private idTemplate: string;

  breadcrumb = [
    { href: '/channel', label: 'channel.breadcrumb.channels' },
    { label: 'channel.templateBuilder.breadcrumb' },
  ];

  constructor(
    injector: Injector,
    private readonly channelService: ChannelService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
    // Read before the form is built: the mode decides the category the form starts with, and the
    // snapshot is already available here — waiting for ngOnInit would mean patching the form after.
    this.pixMode = this.route.snapshot.queryParamMap.get('format') === 'pix';

    this.setupForm();
    this.engine = new TemplateFormEngine(this.formGroup, this.formBuilder, this.pixMode);

    if (this.pixMode) {
      this.breadcrumb = [
        { href: '/channel', label: 'channel.breadcrumb.channels' },
        { label: 'channel.templateBuilder.breadcrumbPix' },
      ];
      this.engine.seedOrderDetailsButton();
    }
  }

  ngOnInit(): void {
    this.idChannel = this.route.snapshot.paramMap.get('idChannel');
    this.idTemplate = this.route.snapshot.paramMap.get('idTemplate');
    this.editing = !!this.idTemplate;

    this.watchForm();
    this.loadChannel(this.idChannel);
    if (this.editing) {
      this.loadTemplate(this.idChannel, this.idTemplate);
    }
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      // Fixed: this page builds Marketing → Standard templates and nothing else, or — in Pix mode —
      // the Utility order details template. The control exists because the provider payload carries
      // a category, not because there is a choice to make.
      category: [this.pixMode ? ORDER_DETAILS_CATEGORY : 'MARKETING', [Validators.required]],

      // identity (RF-09, RF-10)
      name: [null, [Validators.required, Validators.maxLength(this.limits.NAME_MAX_LENGTH), Validators.pattern(/^[a-z0-9_]+$/)]],
      language: ['pt_BR', [Validators.required]],

      // variables (RF-11)
      parameterFormat: ['POSITIONAL'],

      // content (RF-15 … RF-21)
      headerFormat: ['NONE'],
      headerText: [null, [Validators.maxLength(this.limits.HEADER_TEXT_MAX_LENGTH)]],
      headerHandle: [null],
      body: [null, [Validators.required, Validators.maxLength(this.limits.BODY_TEXT_MAX_LENGTH)]],
      footer: [null, [Validators.maxLength(this.limits.FOOTER_TEXT_MAX_LENGTH)]],

      // samples and buttons (RF-23, RF-35 …)
      headerExamples: this.formBuilder.array([]),
      bodyExamples: this.formBuilder.array([]),
      buttons: this.formBuilder.array([]),
    });
  }

  private watchForm(): void {
    this.formGroup.get('headerText').valueChanges
      .pipe(takeUntil(this.destroy$)).subscribe(() => this.engine.syncParameters());
    this.formGroup.get('body').valueChanges
      .pipe(takeUntil(this.destroy$)).subscribe(() => this.engine.syncParameters());
    this.formGroup.get('headerFormat').valueChanges
      .pipe(takeUntil(this.destroy$)).subscribe((format: TemplateHeaderFormatOption) => this.onHeaderFormatChange(format));

    // RF-28 — the save button must reflect the current state without the operator trying first.
    // Recomputed on change rather than read as a getter, so it does not run on every change-detection
    // pass of a form this size.
    this.formGroup.valueChanges
      .pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshProblems());
  }

  /**
   * RF-17 — the header text belongs to the "no media sample" case. Choosing any media clears it and
   * takes it out of the form; returning to None gives it back.
   */
  private onHeaderFormatChange(format: TemplateHeaderFormatOption): void {
    const headerText = this.formGroup.get('headerText');
    if (format === 'NONE') {
      headerText.enable({ emitEvent: false });
    } else {
      headerText.setValue(null, { emitEvent: false });
      headerText.disable({ emitEvent: false });
    }

    if (!this.engine.isMediaHeader(format)) {
      this.formGroup.get('headerHandle').setValue(null, { emitEvent: false });
      this.engine.mediaFileName = null;
      this.engine.currentMediaUrl = null;
    }

    this.engine.syncParameters();
  }

  private loadChannel(idChannel: string): void {
    this.channelService.getById(idChannel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => this.channel = channel,
        error: (err) => this.alertService.error(err?.error?.message),
      });
  }

  private loadTemplate(idChannel: string, idTemplate: string): void {
    this.loading = true;
    this.channelService.getTemplateByChannel(idChannel, idTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (template) => {
          this.template = <WaTemplateRead><unknown>template;

          // Hiding the list action is not enough: the edit route takes any template id. Opening a
          // shape this form cannot represent would drop what it cannot hold and send the loss to
          // Meta on save, so it is refused here too.
          if (!isTemplateEditable(this.template)) {
            this.loading = false;
            this.alertService.error(this.t('channel.templateBuilder.errorNotEditable'));
            this.backToList();
            return;
          }

          this.engine.populate(this.template);
          // RF-32 — name and language cannot change after creation. Disabling is what makes the
          // language select read-only (a `<select>` has no readonly), and `getRawValue()` in
          // buildDefinition still sees both.
          this.formGroup.get('name').disable({ emitEvent: false });
          this.formGroup.get('language').disable({ emitEvent: false });
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.alertService.error(err?.error?.message);
        },
      });
  }

  // ---------------------------------------------------------------------- save

  /**
   * The button stays clickable while the form is incomplete, on purpose.
   *
   * It used to also require `formGroup.valid`, which made `save()` — the only code that explains
   * WHY the form is incomplete — unreachable exactly when it was needed. A variable sample starts
   * empty, required and untouched, so its inline error does not render either: the operator got a
   * greyed-out button, no red field and no warning, with nothing on screen naming the problem.
   *
   * Disabling now only guards against a double submit.
   */
  get canSave(): boolean {
    return !this.submitting;
  }

  refreshProblems(): void {
    this.problems = this.engine.validateRules();
  }

  save(): void {
    this.refreshProblems();

    if (!this.formGroup.valid) {
      // Angular's own recursive walk, not this project's `validateAllFormFields`: that helper treats
      // every FormArray as an array of FormGroups (`Object.keys(control.value)`), which is an empty
      // list for a FormControl. The variable samples are a FormArray of plain controls, so the very
      // fields that most often block saving would stay silent.
      this.formGroup.markAllAsTouched();
      this.alertService.error(this.t('channel.templateBuilder.errorFormIncomplete'));
      return;
    }
    if (this.problems.length > 0) {
      return;
    }

    const definition = this.engine.buildDefinition();
    this.submitting = true;

    const request$: Observable<unknown> = this.editing
      ? this.channelService.updateTemplateByChannel(this.idChannel, this.idTemplate, this.engine.buildEditDefinition(definition, this.template))
      : this.channelService.createTemplateByChannel(this.idChannel, definition);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.submitting = false;
        this.saved = true;
        this.alertService.success(this.t('channel.templateBuilder.savedSuccess'));
        this.backToList();
      },
      error: (err) => {
        this.submitting = false;
        // A refusal keeps the operator on the form with everything typed (RF-30). `validation`
        // carries the provider client's local rule failures; otherwise it is Meta's own message.
        const validation: Array<string> = err?.error?.error?.validation ?? [];
        this.alertService.error(validation.length > 0 ? validation.join(' · ') : err?.error?.message);
      },
    });
  }

  backToList(): void {
    this.router.navigate(['/channel/view', this.idChannel]);
  }

  // ------------------------------------------------------------------ leave guard

  /**
   * RF-03 — confirm before discarding unsaved content. Nothing to confirm once saved, or while the
   * form is still untouched.
   */
  canDeactivate(): boolean | Observable<boolean> {
    if (this.saved || !this.formGroup?.dirty) {
      return true;
    }

    return this.dialog
      .open(ConfirmationComponent, { width: '500px', data: this.t('channel.templateBuilder.leaveConfirmation') })
      .afterClosed()
      .pipe(map((confirmed) => Boolean(confirmed) === true));
  }
}
